import { ReactRenderer } from '@tiptap/react';

import EmojiList from './EmojiList';
import createFloatingPopup from './FloatingPopup';
import MentionList from './MentionList';

const createPopup = ({ props, reactRenderer, placement }) =>
    createFloatingPopup({
        getReferenceClientRect: props.clientRect,
        content: reactRenderer.element,
        placement,
    });

export const getEmojiConfig = ({
    emoji,
    showingEmoji,
    emojisToShowInSuggestions = 30,
    handleCache,
}) => ({
    items: ({ editor, query }) => {
        return editor.storage.emoji.emojis
            .filter(({ shortcodes, tags }) => {
                return (
                    shortcodes.find((shortcode) => shortcode.startsWith(query.toLowerCase())) ||
                    tags.find((tag) => tag.startsWith(query.toLowerCase()))
                );
            })
            .slice(0, emojisToShowInSuggestions);
    },
    allowSpaces: false,
    allowedPrefixes: [' '],
    char: ':',
    // Override extension's default functionality to remove extra trailing space after inserting an emoji
    command: ({ editor, range, props }) => {
        let override;
        const nodeAfter = editor.view.state.selection.$to.nodeAfter;
        const overrideSpace =
            (override = nodeAfter === null || nodeAfter === void 0 ? void 0 : nodeAfter.text) ===
                null || override === void 0
                ? void 0
                : override.startsWith(' ');
        if (overrideSpace) {
            range.to += 1;
        }
        editor
            .chain()
            .focus()
            .insertContentAt(range, [
                {
                    type: 'emoji',
                    attrs: props,
                },
            ])
            .run();
    },
    render: (props) => {
        let reactRenderer;
        let popup;

        return {
            onStart: (props) => {
                props.texts = emoji.texts || {};
                props.maxVisibleItems = emoji.maxVisibleItems;
                props.saveCache = handleCache;

                reactRenderer = new ReactRenderer(EmojiList, {
                    props,
                    editor: props.editor,
                });

                if (!props.clientRect) return;

                showingEmoji.current = true;

                popup = createPopup({ props, reactRenderer, placement: 'top-start' });
            },

            onUpdate(props) {
                if (!reactRenderer) return;
                reactRenderer.updateProps(props);

                popup?.setProps({
                    getReferenceClientRect: props.clientRect,
                });
            },

            onKeyDown(props) {
                if (!reactRenderer) return;
                if (props.event.key === 'Escape') {
                    popup?.hide();
                    reactRenderer.destroy();

                    return true;
                }

                return reactRenderer.ref?.onKeyDown(props);
            },

            onExit() {
                if (!reactRenderer) return;
                popup?.destroy();
                reactRenderer.destroy();
            },
        };
    },
});

export const getSuggestionsConfig = ({ mention, showingMention }) => ({
    items: ({ query }) => mention.fetchSuggestions(query),
    allowedPrefixes: [' '],
    char: '@',
    render: () => {
        let reactRenderer;
        let popup;
        return {
            onStart: (props) => {
                props.texts = mention.texts || {};
                props.maxVisibleItems = mention.maxVisibleItems;
                props.hideNoResultsPopover = mention.hideNoResultsPopover;

                reactRenderer = new ReactRenderer(MentionList, {
                    props,
                    editor: props.editor,
                });

                if (!props.clientRect) return;

                showingMention.current = true;

                popup = createPopup({ props, reactRenderer, placement: 'top-start' });
            },
            onUpdate: (props) => {
                if (!reactRenderer) return;
                reactRenderer.updateProps(props);

                if (!props.clientRect) {
                    return;
                }

                popup?.setProps({
                    getReferenceClientRect: props.clientRect,
                });
            },
            onKeyDown: (props) => {
                if (!reactRenderer) return;
                if (props.event.key === 'Escape') {
                    popup?.hide();
                    return true;
                }

                return reactRenderer.ref?.onKeyDown(props);
            },
            onExit: () => {
                if (!reactRenderer) return;
                popup?.destroy();
                reactRenderer.destroy();
            },
        };
    },
});
